import React, { useState, useEffect } from 'react';
import { useData } from '../hooks/useData';
import { apiFetch } from '../api';
import { LoadingSpinner, Button, PageHeader, Modal, FormInput, FormSelect, StandardPageLayout } from '../components/Common';
import { DynamicIcon } from '../components/icons';
// --- FIX: Import useNotification ---
import { useNotification } from '../context/NotificationContext.jsx';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';



// Form component for editing a partner
const PartnerForm = ({ partner, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        name: partner?.name || '',
        email: partner?.email || '',
        adminEmail: partner?.adminEmail || '',
        domain: partner?.domain || '',
        status: partner?.status || 'Active'
    });
    const handleChange = (e) => setFormData({ ...formData, [e.target.id]: e.target.value });
    const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <FormInput label="Partner Name" id="name" value={formData.name} onChange={handleChange} required />
            <FormInput label="Public Email" id="email" type="email" value={formData.email} onChange={handleChange} required />
            <FormInput label="Admin Email" id="adminEmail" type="email" value={formData.adminEmail} onChange={handleChange} required />
            <FormInput label="Domain" id="domain" value={formData.domain} onChange={handleChange} />
            <FormSelect label="Status" id="status" value={formData.status} onChange={handleChange}>
                <option>Active</option><option>Inactive</option><option>Pending</option>
            </FormSelect>
            <div className="flex justify-end gap-4 pt-4">
                <Button type="button" variant="secondary" onClick={onCancel} withShadow>Cancel</Button>
                <Button type="submit">Save Changes</Button>
            </div>
        </form>
    );
};

const PartnerDetailPage = ({ partnerId, onBack, token }) => {
    const { data: partner, isLoading, error, refresh } = useData(`/partners/${partnerId}`, token);
    useBreadcrumbTitle(partner?.name || null);

    const { formatDate } = useTimezoneFormatter();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const { addNotification } = useNotification();

    const handleSave = async (partnerData) => {
        try {
            await apiFetch(`/partners/${partnerId}`, token, {
                method: 'PUT',
                body: JSON.stringify(partnerData)
            });
            addNotification(`Partner "${partnerData.name}" updated successfully.`, 'success');
            setIsEditModalOpen(false);
            refresh();
        } catch (err) {
            addNotification("Error updating partner: " + err.message, 'error');
        }
    };

    const handleDelete = async () => {
        try {
            await apiFetch(`/partners/${partnerId}`, token, { method: 'DELETE' });
            addNotification(`Partner "${partner.name}" deactivated.`, 'success');
            onBack();
        } catch (err) {
            addNotification("Error deactivating partner: " + err.message, 'error');
        }
    };

    if (isLoading) return <LoadingSpinner />;
    if (error) return <div className="text-red-500 p-4 rounded-md bg-red-50">Error loading partner details: {error.message}</div>;
    if (!partner) return <div>Partner not found.</div>;

    const headerActions = (
        <div className="flex items-center gap-2">
            <Button onClick={onBack} variant="secondary" sx={{ height: 32 }}>
                <DynamicIcon name="ChevronLeft" className="w-4 h-4 mr-2" />
                All Partners
            </Button>
            <Button onClick={() => setIsEditModalOpen(true)} variant="secondary" sx={{ height: 32 }}>
                <DynamicIcon name="Edit" className="w-4 h-4 mr-2" />
                Edit
            </Button>
            <Button onClick={handleDelete} variant="danger" sx={{ height: 32 }}>
                <DynamicIcon name="Trash2" className="w-4 h-4 mr-2" />
                Deactivate
            </Button>
        </div>
    );

    return (
        <StandardPageLayout
            title={partner.name || 'Partner Details'}
            description="View and manage partner details."
            headerActions={headerActions}
        >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold mb-4">Partner Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div><p className="font-semibold text-gray-500">Contact Email</p><p>{partner.email}</p></div>
                    <div><p className="font-semibold text-gray-500">Admin Email</p><p>{partner.adminEmail}</p></div>
                    <div><p className="font-semibold text-gray-500">Domain</p><p>{partner.domain}</p></div>
                    <div><p className="font-semibold text-gray-500">Status</p><p>{partner.status}</p></div>
                    <div><p className="font-semibold text-gray-500">Join Date</p><p>{formatDate(partner.joinDate)}</p></div>
                    <div><p className="font-semibold text-gray-500">Tenant Count</p><p>{partner.tenantCount}</p></div>
                </div>
            </div>

            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title={`Edit ${partner.name}`}>
                <PartnerForm partner={partner} onSave={handleSave} onCancel={() => setIsEditModalOpen(false)} />
            </Modal>
        </StandardPageLayout>
    );
};

export default PartnerDetailPage;